import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Scale } from 'lucide-react'
import { INTL_LOCALE, useLocale } from '../lib/i18n.tsx'
import { PhaseBadge, type Phase } from './badges.tsx'
import { Kbd } from './Kbd.tsx'
import { cn } from './util.ts'

export interface ScreenProps {
  /** Route name, written to data-screen (the shell's scripts and the feature tests read it). */
  name: string
  /** Minimum number of [data-item] elements a smoke test expects on this screen. */
  minItems?: number
  /** Full-bleed content (graph canvas, board): no page padding or max width. */
  bleed?: boolean
  className?: string
  children: ReactNode
}

/** Root of every screen: renders <main data-screen data-min-items>. */
export function Screen({ name, minItems = 0, bleed, className, children }: ScreenProps) {
  return (
    <main
      id="main"
      tabIndex={-1}
      data-screen={name}
      data-min-items={minItems}
      className={cn(
        'vt-screen min-w-0 flex-1 outline-none',
        !bleed && 'mx-auto w-full max-w-page px-4 pt-5 pb-10 md:px-8 md:pt-7',
        className,
      )}
    >
      {children}
    </main>
  )
}

export interface PageHeaderProps {
  title: ReactNode
  /** Small mono line above the title, e.g. 'Organisations · 612'. */
  eyebrow?: ReactNode
  icon?: LucideIcon
  description?: ReactNode
  phase?: Phase | Phase[]
  /** Extra badges after the title (LayerBadge, Chip). */
  badges?: ReactNode
  actions?: ReactNode
  className?: string
}

/** Title block of a screen with phase badges and actions. */
export function PageHeader({ title, eyebrow, icon: Icon, description, phase, badges, actions, className }: PageHeaderProps) {
  const phases = phase === undefined ? [] : Array.isArray(phase) ? phase : [phase]
  return (
    <header className={cn('flex flex-col gap-4 pb-6 md:flex-row md:items-end md:justify-between', className)}>
      <div className="min-w-0">
        {(eyebrow || Icon) && (
          <div className="mb-2 flex items-center gap-2 text-ink-faint">
            {Icon && <Icon size={14} strokeWidth={1.75} aria-hidden />}
            {eyebrow && <span className="label-mono">{eyebrow}</span>}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="font-display text-2xl font-semibold text-ink">{title}</h1>
          {(phases.length > 0 || badges) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {phases.map((p) => (
                <PhaseBadge key={p} phase={p} />
              ))}
              {badges}
            </div>
          )}
        </div>
        {description && <p className="mt-2 max-w-2xl text-base text-ink-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  )
}

export interface PanelProps {
  title?: ReactNode
  icon?: LucideIcon
  actions?: ReactNode
  /** Remove body padding (tables, lists that run edge to edge). */
  flush?: boolean
  className?: string
  children?: ReactNode
}

/** A titled section card: <Panel title="Signals" actions={…}>…</Panel>. */
export function Panel({ title, icon: Icon, actions, flush, className, children }: PanelProps) {
  return (
    <section className={cn('flex min-w-0 flex-col rounded-lg bg-raised shadow-raised ring-1 ring-line ring-inset', className)}>
      {(title || actions) && (
        <div className="flex min-h-11 items-center justify-between gap-3 border-b border-line px-4 py-2">
          <h2 className="flex min-w-0 items-center gap-2 font-sans text-sm font-semibold text-ink">
            {Icon && <Icon size={15} strokeWidth={1.75} aria-hidden className="text-ink-faint" />}
            <span className="truncate">{title}</span>
          </h2>
          {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
        </div>
      )}
      <div className={cn('min-w-0 flex-1', !flush && 'p-4')}>{children}</div>
    </section>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: LucideIcon
  kbd?: string[]
}

/** Text field in kit style: <Input icon={Search} placeholder="Filter" kbd={['/']} />. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ icon: Icon, kbd, className, ...rest }, ref) {
  return (
    <span
      className={cn(
        'flex h-9 min-w-0 items-center gap-2 rounded-md bg-sunken px-3 ring-1 ring-line-strong transition ring-inset',
        'focus-within:ring-2 focus-within:ring-focus',
        className,
      )}
    >
      {Icon && <Icon size={15} strokeWidth={1.75} aria-hidden className="shrink-0 text-ink-faint" />}
      <input
        ref={ref}
        className="h-full min-w-0 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-ink-faint focus-visible:outline-none"
        {...rest}
      />
      {kbd && <Kbd keys={kbd} tone="plain" />}
    </span>
  )
})

/** The licence line required on person, organisation and notice pages. */
export function NlodLine({ className }: { className?: string }) {
  const { t } = useLocale()
  return (
    <p className={cn('flex items-start gap-2 text-xs text-ink-faint', className)}>
      <Scale size={13} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
      <span>{t('common.nlod')}</span>
    </p>
  )
}

export interface SpendMeterProps {
  used: number
  limit: number
  label?: string
  unit?: string
  className?: string
}

/** Monthly model spend against its limit: amber from 80 %, red at the limit. */
export function SpendMeter({ used, limit, label, unit = 'NOK', className }: SpendMeterProps) {
  const { t, locale } = useLocale()
  const text = label ?? t('common.spendLabel')
  const nok = new Intl.NumberFormat(INTL_LOCALE[locale], { maximumFractionDigits: 0 })
  const share = limit > 0 ? Math.min(1, used / limit) : 0
  const tone = share >= 1 ? 'bg-danger' : share >= 0.8 ? 'bg-warn' : 'bg-ok'
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="text-ink-muted">{text}</span>
        <span className="font-mono text-ink tabular">
          {unit} {nok.format(used)}
          <span className="text-ink-faint"> / {nok.format(limit)}</span>
        </span>
      </div>
      <div
        role="meter"
        aria-label={text}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={used}
        className="relative h-1.5 overflow-hidden rounded-full bg-hover"
      >
        <span className={cn('absolute inset-y-0 left-0 rounded-full', tone)} style={{ width: `${share * 100}%` }} />
        <span aria-hidden className="absolute inset-y-0 left-4/5 w-px bg-line-strong" />
      </div>
    </div>
  )
}
